/*
Origin Core 1.0

called from /layout/theme.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <localization-form> that manages country and language selection for Shopify localization. 
It handles searching, filtering, and selecting countries, updates the displayed selection, and manages the modal dialog for the selector. 
The script fetches localization suggestions via AJAX, determines the appropriate market, and shows a geolocation popup if needed. 
It also manages user preferences for showing the popup using cookies and provides keyboard accessibility for the selector UI. 
The component is registered if not already defined.

*/

if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelectorAll('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          link: this.querySelectorAll('.disclosure__item'),
          continueButton: this.querySelector('button[type="submit"]'),
          shipTo: this.querySelector('#country_selected')
        };

        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }
        if (this.elements.continueButton) {
          this.elements.continueButton.addEventListener('click', this.submitContinueButton.bind(this));
        }

        this.elements.link.forEach(item => {  
          item.addEventListener('click', (event) => {

           let selectedLink = item.closest('.disclosure__list').querySelector('.selected');
           if (this.elements.shipTo) {
            this.elements.shipTo.textContent = item.querySelector('.country').textContent;
           }
           if (selectedLink) {
            selectedLink.classList.remove('selected');
           }
            event.target.closest('.disclosure__item').classList.add('selected');
            this.elements.input.value = item.querySelector('.disclosure__link').dataset.value;
          });
        });

        // Disclosure button opens the dialog modally
        // if (this.elements.button) {
        //   this.elements.button.forEach(filter => {
        //     filter.addEventListener("click", () => {
        //       this.elements.panel.showModal();
        //       this.elements.panel.classList.add('open');
        //     });
        //   });
        // }

        // Close the dialog button or click outside
        if (this.elements.closeButton) {
          document.addEventListener("keydown", ({key}) => {
            if (key === "Escape") {
              this.elements.panel.classList.remove('open');
              setTimeout(() => this.elements.panel.close(), 500);
            }
          });
          this.elements.closeButton.addEventListener('click', ()=> {
            this.elements.panel.classList.remove('open');
            setTimeout(() => this.elements.panel.close(), 500);
          })
        }
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('.disclosure__link')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      submitContinueButton() {
        const form = this.querySelector('form');
        if (form) form.submit();
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('.disclosure__link');
        let visibleCountries = allCountries.length;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );


  // Market constructor
  const market = {};
  const markets = [];

  // URL (to determine market switch or expansion store redirect)
  function Market(name, countries, storeName, url) {
    this.name = name;
    this.countries = countries;
    this.storeName = storeName ? storeName : null;
    this.url = url ? url : null;
  }

  // set up current markets
  const globalMarket = new Market('uk', ['GB'], 'UK Store', 'https://unified-origin.myshopify.com');
  markets.push(globalMarket);

  const euMarket = new Market('eu', ['AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK'], 'EU Store', 'https://unified-origin.myshopify.com/en-eu');
  markets.push(euMarket);

  const usMarket = new Market('us', ['US','CA'], 'US Store', 'https://unified-origin.myshopify.com/en-us/');
  markets.push(usMarket);

  async function fetchRoutes() {
    const response = await fetch(
      window.Shopify.routes.root
        + 'browsing_context_suggestions.json'
        + '?country[enabled]=true'
        + `&country[exclude]=${window.Shopify.country}`
        + '&language[enabled]=true'
        + `&language[exclude]=${window.Shopify.language}`
    );
    return response.json();
  }

  function showMarketSelectorPopup(newMarket, country_code, country_name ){

    // Show popup dispatch event
    var customEvent = new CustomEvent('geolocator-show', {
      detail: { 
        open: true,
        newMarket: newMarket,
        country_code: country_code,
        country_name: country_name
      }
    });
    window.dispatchEvent(customEvent);
  }

  fetchRoutes().then(res => {
      const country_handle = res?.detected_values?.country?.handle,
          country_lang = res?.suggestions[0]?.parts?.language?.handle,
          country_name = res.detected_values.country_name,
          // what market is the current localization liquid object in?
          localizationInMarket = markets.find(market => market.countries.includes(country_handle)),
          // Call to start the geolocation
          // check to see if we should show the popup baed on cookie
          show_geo_popup = getCookie('show_geo_popup');

      if (show_geo_popup == null || show_geo_popup) {
          if (country_handle != Shopify.country) {
              showMarketSelectorPopup(localizationInMarket, country_handle, country_name);
          }
        }
  });

  // User has chosen to ignore the popup
  // cookie this preference and don't show the popup again for 12 hrs
  window.addEventListener('cookie_geo_preference', function(){
    setCookie('show_geo_popup', false, 0.5);
  }); 

  function setCookie(name,value,days) {
    var expires = "";
    if (days) {
        var date = new Date();
        date.setTime(date.getTime() + (days*24*60*60*1000));
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "")  + expires + "; path=/";
  }
  function getCookie(name) {
    var nameEQ = name + "=";
    var ca = document.cookie.split(';');
    for(var i=0;i < ca.length;i++) {
        var c = ca[i];
        while (c.charAt(0)==' ') c = c.substring(1,c.length);
        if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
    }
    return null;
  }
  function eraseCookie(name) {   
    document.cookie = name +'=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }

}
  